import React, { useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Undo, Redo } from 'lucide-react';
import { convertMarkersToHTML, convertHTMLToMarkers } from '../utils/markerConverter';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
}

// Imperative handle so a parent can read the editor's CURRENT text at save time,
// straight from the live document — never from React state that may not have caught
// up with the latest keystroke yet. This is what makes section-edit saves reliable.
export interface RichTextEditorHandle {
  getContent: () => string;
}

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(({ content, onChange, placeholder = 'Start typing...' }, ref) => {
  const isUpdatingRef = React.useRef(false);
  const initialContentRef = React.useRef(content);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder
      })
    ],
    content: convertMarkersToHTML(content),
    onUpdate: ({ editor }) => {
      // Set flag to prevent update loop
      isUpdatingRef.current = true;

      // Convert HTML back to text with markers before passing to parent
      const htmlContent = editor.getHTML();
      const textContent = convertHTMLToMarkers(htmlContent);
      onChange(textContent);

      // Reset flag after render cycle completes
      requestAnimationFrame(() => {
        isUpdatingRef.current = false;
      });
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-3',
        style: 'white-space: pre-wrap; word-wrap: break-word; line-height: 1.8;'
      }
    }
  });

  // Expose the live editor text to the parent. Reading here (not from the debounced
  // onChange state) guarantees a Save always persists exactly what's on screen.
  useImperativeHandle(ref, () => ({
    getContent: () => (editor ? convertHTMLToMarkers(editor.getHTML()) : content),
  }), [editor, content]);

  // Update editor content ONLY when content changes externally (not from user typing)
  useEffect(() => {
    if (!editor || isUpdatingRef.current) return;

    const currentEditorText = convertHTMLToMarkers(editor.getHTML());

    // Only update if content is different and not from our own onChange
    if (content !== currentEditorText && content !== initialContentRef.current) {
      const newContent = convertMarkersToHTML(content);
      editor.commands.setContent(newContent, { emitUpdate: false });
      initialContentRef.current = content;
    }
  }, [content, editor]);

  const toggleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run();
  }, [editor]);

  const toggleBulletList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run();
  }, [editor]);

  const toggleOrderedList = useCallback(() => {
    editor?.chain().focus().toggleOrderedList().run();
  }, [editor]);

  const setHeading1 = useCallback(() => {
    editor?.chain().focus().toggleHeading({ level: 1 }).run();
  }, [editor]);

  const setHeading2 = useCallback(() => {
    editor?.chain().focus().toggleHeading({ level: 2 }).run();
  }, [editor]);

  const undo = useCallback(() => {
    editor?.chain().focus().undo().run();
  }, [editor]);

  const redo = useCallback(() => {
    editor?.chain().focus().redo().run();
  }, [editor]);

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Custom styles for markers */}
      <style>{`
        .ProseMirror span[data-marker] {
          display: inline-flex !important;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 500;
          margin: 0 0.25rem;
          white-space: nowrap;
          cursor: default;
        }

        .ProseMirror span[data-marker="controlPoint"] {
          background-color: #fef9c3;
          color: #854d0e;
          border: 1px solid #facc15;
        }

        .ProseMirror span[data-marker="gapIdentified"] {
          background-color: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }

        .ProseMirror span[data-marker="screenshotNeeded"] {
          background-color: #dbeafe;
          color: #1e3a8a;
          border: 1px solid #3b82f6;
        }

        .ProseMirror span[data-marker="flowchartNeeded"] {
          background-color: #d1fae5;
          color: #065f46;
          border: 1px solid #10b981;
        }

        .ProseMirror span[data-marker="conflict"] {
          background-color: #fed7aa;
          color: #9a3412;
          border: 1px solid #f97316;
        }

        .ProseMirror span[data-marker="assumption"] {
          background-color: #f3e8ff;
          color: #6b21a8;
          border: 1px solid #a855f7;
        }

        .ProseMirror span[data-marker="source"] {
          background-color: #f3f4f6;
          color: #374151;
          border: 1px solid #9ca3af;
        }

        .ProseMirror span[data-marker="documented"] {
          background-color: #dcfce7;
          color: #166534;
          border: 1px solid #22c55e;
        }

        .ProseMirror span[data-marker="inferred"] {
          background-color: #dbeafe;
          color: #1e3a8a;
          border: 1px solid #3b82f6;
        }

        .ProseMirror span[data-marker="assumed"] {
          background-color: #fef3c7;
          color: #92400e;
          border: 1px solid #fbbf24;
        }

        .ProseMirror p {
          margin-bottom: 0.5rem;
        }

        .ProseMirror {
          line-height: 1.6;
        }
      `}</style>

      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 px-3 py-2 flex items-center space-x-1">
        <button
          onClick={toggleBold}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}
          title="Bold"
          type="button"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={toggleItalic}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}
          title="Italic"
          type="button"
        >
          <Italic className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        <button
          onClick={setHeading1}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''}`}
          title="Heading 1"
          type="button"
        >
          <Heading1 className="w-4 h-4" />
        </button>
        <button
          onClick={setHeading2}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''}`}
          title="Heading 2"
          type="button"
        >
          <Heading2 className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        <button
          onClick={toggleBulletList}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}
          title="Bullet List"
          type="button"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          onClick={toggleOrderedList}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}
          title="Numbered List"
          type="button"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        <button
          onClick={undo}
          className="p-2 rounded hover:bg-gray-200 transition-colors"
          title="Undo"
          type="button"
        >
          <Undo className="w-4 h-4" />
        </button>
        <button
          onClick={redo}
          className="p-2 rounded hover:bg-gray-200 transition-colors"
          title="Redo"
          type="button"
        >
          <Redo className="w-4 h-4" />
        </button>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} className="bg-white" />
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
